'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import formatName from 'I18n/utils/formatName';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UISelectableButton from 'UIComponents/button/UISelectableBox';
import Small from 'UIComponents/elements/Small';
import { UNAVAILABLE_REASONS } from 'MeetingsLib/constants/enums';
import { getPrimaryColorOrDefault } from 'MeetingsBase/utils/color';
import { getColorStyleWithOpacity } from 'MeetingsLib/utils/color';
import PreviewContext from 'MeetingsLib/utils/PreviewContext';
import Branding from 'MeetingsBase/models/Branding';
import { ConflictingEventRecord } from 'MeetingsBase/models/TroubleshootTimeInterval';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class TimePickerButton extends Component {
  constructor(props) {
    super(props);
    this.onMouseEnter = () => {
      this.setState({
        hover: true
      });
    };
    this.onMouseLeave = () => {
      this.setState({
        hover: false
      });
    };
    this.getBrandingActiveStyle = () => {
      const {
        branding: {
          primaryColor
        },
        time,
        selectedTime
      } = this.props;
      const {
        hover
      } = this.state;
      if (!hover && time !== selectedTime) {
        return null;
      }
      return {
        borderColor: primaryColor,
        backgroundColor: getColorStyleWithOpacity(primaryColor)
      };
    };
    this.getBrandingTextStyle = () => {
      const {
        branding: {
          primaryColor
        }
      } = this.props;
      if (!primaryColor) {
        return null;
      }
      return {
        color: getPrimaryColorOrDefault(primaryColor)
      };
    };
    this.getOwnerName = () => {
      const {
        conflictingEvent: {
          firstName,
          lastName,
          email
        }
      } = this.props;
      const formattedName = formatName({
        firstName,
        lastName
      });
      return formattedName || email;
    };
    this.getUnavailableMessageProps = () => {
      const {
        unavailableReason,
        conflictingEvent: {
          relatedEventStartTimeMillis
        }
      } = this.props;
      const ownerName = this.getOwnerName();
      switch (unavailableReason) {
        case UNAVAILABLE_REASONS.PAST:
          return {
            message: 'troubleshoot.past'
          };
        case UNAVAILABLE_REASONS.ALL_DAY_EVENT:
          return {
            message: 'troubleshoot.allDayBusy',
            options: {
              ownerName
            }
          };
        case UNAVAILABLE_REASONS.EVENT:
          return {
            message: 'troubleshoot.busy',
            options: {
              ownerName
            }
          };
        case UNAVAILABLE_REASONS.OUTSIDE_WORKING_HOURS:
          return {
            message: 'troubleshoot.outsideWorkingHours'
          };
        case UNAVAILABLE_REASONS.BUFFER:
          return {
            message: 'troubleshoot.buffer',
            options: {
              relatedEventStartTime: this.getLowercaseTime(relatedEventStartTimeMillis)
            }
          };
        case UNAVAILABLE_REASONS.MINIMUM_NOTICE:
          return {
            message: 'troubleshoot.minimumNotice'
          };
        default:
          return {
            message: 'troubleshoot.default'
          };
      }
    };
    this.getLowercaseTime = timestamp => {
      const {
        userTimezone
      } = this.props;
      const timeOfDay = I18n.moment(timestamp).tz(userTimezone).format('LT');
      return timeOfDay.toLowerCase();
    };
    this.renderUnavailableReason = () => {
      const {
        disabled
      } = this.props;
      if (!disabled) {
        return null;
      }
      const text = /*#__PURE__*/_jsx(FormattedReactMessage, Object.assign({}, this.getUnavailableMessageProps()));
      return /*#__PURE__*/_jsx(UITruncateString, {
        tooltip: /*#__PURE__*/_jsx(Small, {
          children: text
        }),
        children: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: text
        })
      });
    };
    this.renderMinutes = () => {
      const {
        time,
        selectedDate
      } = this.props;
      const lowerCaseTimeOfDay = this.getLowercaseTime(time);
      return /*#__PURE__*/_jsx("span", {
        "aria-label": I18n.text('prebook.timePicker.timePickerButtonLabel', {
          timeOption: lowerCaseTimeOfDay,
          selectedDate: I18n.moment(selectedDate).format('MMMM Do')
        }),
        style: this.getBrandingTextStyle(),
        children: lowerCaseTimeOfDay
      });
    };
    this.state = {
      hover: false
    };
  }
  render() {
    const {
      disabled,
      handleSetEventDateTime,
      selectedTime,
      time
    } = this.props;
    return /*#__PURE__*/_jsx(View, {
      paddingBlock: "100",
      paddingInlineEnd: "100",
      as: "div",
      children: /*#__PURE__*/_jsx(PreviewContext.Consumer, {
        children: isPreview => /*#__PURE__*/_jsxs(UISelectableButton, {
          disabled: disabled,
          block: true,
          padded: false,
          className: "time-picker-btn",
          "data-test-id": 'time-picker-btn',
          "data-timestamp": time,
          selected: selectedTime === time,
          style: this.getBrandingActiveStyle(),
          onMouseEnter: this.onMouseEnter,
          onMouseLeave: this.onMouseLeave,
          onClick: isPreview ? null : handleSetEventDateTime,
          children: [this.renderMinutes(), /*#__PURE__*/_jsx("br", {}), this.renderUnavailableReason()]
        })
      })
    });
  }
}
TimePickerButton.propTypes = {
  branding: PropTypes.instanceOf(Branding).isRequired,
  disabled: PropTypes.bool,
  handleSetEventDateTime: PropTypes.func.isRequired,
  // TODO: Convert file to TS & functional component.
  // Instance of ConflictingEvent in TroubleshootTimesByDuration
  conflictingEvent: PropTypes.instanceOf(ConflictingEventRecord),
  selectedTime: PropTypes.number,
  time: PropTypes.number.isRequired,
  unavailableReason: PropTypes.string,
  userTimezone: PropTypes.string.isRequired,
  selectedDate: PropTypes.string.isRequired
};